import { useState, useEffect, useCallback } from 'react';
import { hospitalApi } from '../../hospitalApi';

const C = '#1b6ca8';
const DARK = '#0f4c75';
const LIGHT = '#e8f4fd';

const EMPTY_TREAT = {
  patientId: '', doctorId: '', visitDate: '', diagnosis: '',
  treatmentNotes: '', followUpDate: '',
};

const EMPTY_RX = {
  medicineName: '', dosage: '', frequency: '', duration: '', instructions: '',
};

const inp = { className: 'form-control' };

function F({ label, children }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

export default function HospitalTreatments({ role }) {
  const [tab, setTab]                 = useState('history');
  const [patients, setPatients]       = useState([]);
  const [doctors, setDoctors]         = useState([]);
  const [selectedPat, setSelectedPat] = useState(null);
  const [patSearch, setPatSearch]     = useState('');
  const [treatments, setTreatments]   = useState([]);
  const [expanded, setExpanded]       = useState(null);
  const [prescriptions, setPrescs]    = useState({});
  const [form, setForm]               = useState(EMPTY_TREAT);
  const [rxForms, setRxForms]         = useState([{ ...EMPTY_RX }]);
  const [saving, setSaving]           = useState(false);
  const [err, setErr]                 = useState('');

  useEffect(() => {
    hospitalApi.getDoctors().then(d => { if (d) setDoctors(d.filter(doc => doc.status === 'active')); });
    hospitalApi.getPatients().then(d => { if (d) setPatients(d); });
  }, []);

  const loadTreatments = useCallback((patId) => {
    if (!patId) { setTreatments([]); return; }
    hospitalApi.getTreatments(patId).then(d => { if (d) setTreatments(d); });
  }, []);

  useEffect(() => { loadTreatments(selectedPat); }, [selectedPat, loadTreatments]);

  const toggleExpand = async (tid) => {
    if (expanded === tid) { setExpanded(null); return; }
    setExpanded(tid);
    if (!prescriptions[tid]) {
      const rx = await hospitalApi.getPrescriptions(tid);
      if (rx) setPrescs(prev => ({ ...prev, [tid]: rx }));
    }
  };

  const handleSelectPat = (p) => {
    setSelectedPat(p.id);
    setPatSearch(p.name);
    setForm(f => ({ ...f, patientId: String(p.id) }));
  };

  const handleSaveTreatment = async () => {
    if (!form.patientId || !form.doctorId || !form.visitDate) {
      setErr('Patient, Doctor, and Visit Date are required');
      return;
    }
    setSaving(true); setErr('');
    try {
      const res = await hospitalApi.createTreatment({
        ...form,
        patientId: parseInt(form.patientId),
        doctorId: parseInt(form.doctorId),
      });
      if (res && res.error) { setErr(res.error); setSaving(false); return; }
      // Add prescriptions - need to get the new treatment id
      // Reload treatments and add prescriptions to latest
      const freshTreatments = await hospitalApi.getTreatments(parseInt(form.patientId));
      if (freshTreatments && freshTreatments.length > 0 && rxForms.some(rx => rx.medicineName)) {
        const newTid = freshTreatments[0].id;
        for (const rx of rxForms) {
          if (rx.medicineName) {
            await hospitalApi.addPrescription(newTid, rx);
          }
        }
      }
      setTreatments(freshTreatments || []);
      setForm({ ...EMPTY_TREAT, patientId: form.patientId });
      setRxForms([{ ...EMPTY_RX }]);
      setTab('history');
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const deletePrescription = async (prescId, treatId) => {
    if (!window.confirm('Delete prescription?')) return;
    await hospitalApi.deletePrescription(prescId);
    const fresh = await hospitalApi.getPrescriptions(treatId);
    if (fresh) setPrescs(prev => ({ ...prev, [treatId]: fresh }));
  };

  const addRxRow = () => setRxForms(prev => [...prev, { ...EMPTY_RX }]);
  const updateRx = (i, key, val) => setRxForms(prev => prev.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  const removeRx = (i) => setRxForms(prev => prev.filter((_, idx) => idx !== i));

  const filtPats = patients.filter(p =>
    !patSearch || p.name?.toLowerCase().includes(patSearch.toLowerCase()) || p.phone?.includes(patSearch)
  );

  return (
    <div>
      {/* Patient search */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ position: 'relative', maxWidth: 360 }}>
          <input className="form-control" placeholder="Search patient to view/add treatments…"
            value={patSearch}
            onChange={e => { setPatSearch(e.target.value); setSelectedPat(null); setForm(f => ({ ...f, patientId: '' })); }} />
          {patSearch && !selectedPat && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff',
              border: '1px solid #e8eaed', borderRadius: 6, maxHeight: 200, overflowY: 'auto', zIndex: 10,
              boxShadow: '0 4px 12px rgba(0,0,0,.12)' }}>
              {filtPats.slice(0, 8).map(p => (
                <div key={p.id} onClick={() => handleSelectPat(p)}
                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f1f3f4' }}
                  onMouseEnter={e => e.currentTarget.style.background = LIGHT}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  {p.name} — {p.phone || 'No phone'}
                </div>
              ))}
              {filtPats.length === 0 && <div style={{ padding: 10, color: '#9aa0a6', fontSize: 13 }}>No patients found</div>}
            </div>
          )}
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: 16 }}>
        {[
          { key: 'history', label: '📋 Treatment History' },
          { key: 'add', label: '➕ Add Treatment' },
        ].map(t => (
          <button key={t.key} className={`tab${tab === t.key ? ' active' : ''}`} onClick={() => { setTab(t.key); setErr(''); }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── History Tab ── */}
      {tab === 'history' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              {selectedPat ? `Treatments for ${patSearch}` : 'Select a patient to view treatments'}
            </div>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {treatments.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#9aa0a6' }}>
                {selectedPat ? 'No treatments recorded for this patient' : 'Search and select a patient above'}
              </div>
            ) : (
              treatments.map((t, i) => (
                <div key={t.id} style={{ borderTop: i > 0 ? '1px solid #f1f3f4' : 'none' }}>
                  <div
                    style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex',
                      justifyContent: 'space-between', alignItems: 'center',
                      background: expanded === t.id ? LIGHT : 'transparent' }}
                    onClick={() => toggleExpand(t.id)}
                  >
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: DARK }}>{t.diagnosis || 'No diagnosis noted'}</div>
                      <div style={{ fontSize: 12, color: '#5f6368', marginTop: 2 }}>
                        {t.visit_date} · Dr. {t.doctor_name || '—'}
                        {t.follow_up_date && <span> · Follow-up: {t.follow_up_date}</span>}
                      </div>
                    </div>
                    <span style={{ fontSize: 12, color: C, fontWeight: 600 }}>
                      {expanded === t.id ? '▲ Hide' : '▼ Show'} prescriptions
                    </span>
                  </div>
                  {expanded === t.id && (
                    <div style={{ padding: '8px 16px 16px', background: '#fafbfc', borderTop: '1px solid #f1f3f4' }}>
                      {t.treatment_notes && (
                        <div style={{ fontSize: 13, color: '#5f6368', marginBottom: 10, fontStyle: 'italic' }}>
                          {t.treatment_notes}
                        </div>
                      )}
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: DARK, marginBottom: 6 }}>💊 Prescriptions</div>
                        {(prescriptions[t.id] || []).length === 0 ? (
                          <div style={{ fontSize: 12, color: '#9aa0a6' }}>No prescriptions for this treatment</div>
                        ) : (
                          (prescriptions[t.id] || []).map(rx => (
                            <div key={rx.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
                              padding: '6px 10px', background: '#fff', borderRadius: 6,
                              border: '1px solid #e8eaed', marginBottom: 4 }}>
                              <div style={{ flex: 1, fontSize: 13 }}>
                                <strong>{rx.medicine_name}</strong> — {rx.dosage} · {rx.frequency} · {rx.duration}
                                {rx.instructions && <span style={{ color: '#888' }}> ({rx.instructions})</span>}
                              </div>
                              <button style={{ fontSize: 11, padding: '2px 7px', background: '#fce8e6',
                                color: '#c5221f', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                                onClick={() => deletePrescription(rx.id, t.id)}>✕</button>
                            </div>
                          ))
                        )}
                      </div>
                      <AddPrescriptionInline treatmentId={t.id} onAdded={() => {
                        hospitalApi.getPrescriptions(t.id).then(rx => {
                          if (rx) setPrescs(prev => ({ ...prev, [t.id]: rx }));
                        });
                      }} C={C} DARK={DARK} LIGHT={LIGHT} />
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ── Add Treatment Tab ── */}
      {tab === 'add' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Add New Treatment</div>
          </div>
          <div className="card-body">
            {err && <div style={{ color: '#c5221f', background: '#fce8e6', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>⚠️ {err}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
              <F label="Patient *">
                <select {...inp} value={form.patientId} onChange={e => setForm(f => ({ ...f, patientId: e.target.value }))}>
                  <option value="">Select Patient</option>
                  {patients.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                </select>
              </F>
              <F label="Doctor *">
                <select {...inp} value={form.doctorId} onChange={e => setForm(f => ({ ...f, doctorId: e.target.value }))}>
                  <option value="">Select Doctor</option>
                  {doctors.map(d => <option key={d.id} value={String(d.id)}>Dr. {d.name}</option>)}
                </select>
              </F>
              <F label="Visit Date *">
                <input {...inp} type="date" value={form.visitDate} onChange={e => setForm(f => ({ ...f, visitDate: e.target.value }))} />
              </F>
              <F label="Follow-up Date">
                <input {...inp} type="date" value={form.followUpDate} onChange={e => setForm(f => ({ ...f, followUpDate: e.target.value }))} />
              </F>
              <div className="form-group" style={{ gridColumn: '1/-1' }}>
                <label className="form-label">Diagnosis</label>
                <textarea {...inp} rows={2} value={form.diagnosis}
                  onChange={e => setForm(f => ({ ...f, diagnosis: e.target.value }))} style={{ resize: 'vertical' }} />
              </div>
              <div className="form-group" style={{ gridColumn: '1/-1' }}>
                <label className="form-label">Treatment Notes</label>
                <textarea {...inp} rows={3} value={form.treatmentNotes}
                  onChange={e => setForm(f => ({ ...f, treatmentNotes: e.target.value }))} style={{ resize: 'vertical' }} />
              </div>
            </div>

            {/* Prescriptions */}
            <div style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: DARK }}>💊 Prescriptions</div>
                <button onClick={addRxRow}
                  style={{ fontSize: 12, padding: '4px 12px', background: LIGHT, color: DARK,
                    border: `1px solid ${C}`, borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                  + Add Medicine
                </button>
              </div>
              {rxForms.map((rx, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 2fr auto', gap: 8, marginBottom: 8, alignItems: 'end' }}>
                  <div>
                    {i === 0 && <label className="form-label" style={{ fontSize: 11 }}>Medicine Name</label>}
                    <input {...inp} placeholder="Medicine name" value={rx.medicineName}
                      onChange={e => updateRx(i, 'medicineName', e.target.value)} />
                  </div>
                  <div>
                    {i === 0 && <label className="form-label" style={{ fontSize: 11 }}>Dosage</label>}
                    <input {...inp} placeholder="e.g. 500mg" value={rx.dosage}
                      onChange={e => updateRx(i, 'dosage', e.target.value)} />
                  </div>
                  <div>
                    {i === 0 && <label className="form-label" style={{ fontSize: 11 }}>Frequency</label>}
                    <input {...inp} placeholder="e.g. 3x/day" value={rx.frequency}
                      onChange={e => updateRx(i, 'frequency', e.target.value)} />
                  </div>
                  <div>
                    {i === 0 && <label className="form-label" style={{ fontSize: 11 }}>Duration</label>}
                    <input {...inp} placeholder="e.g. 7 days" value={rx.duration}
                      onChange={e => updateRx(i, 'duration', e.target.value)} />
                  </div>
                  <div>
                    {i === 0 && <label className="form-label" style={{ fontSize: 11 }}>Instructions</label>}
                    <input {...inp} placeholder="After meals, etc." value={rx.instructions}
                      onChange={e => updateRx(i, 'instructions', e.target.value)} />
                  </div>
                  <div>
                    {i === 0 && <div style={{ height: 22 }} />}
                    <button onClick={() => removeRx(i)}
                      style={{ padding: '7px 10px', background: '#fce8e6', color: '#c5221f',
                        border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>✕</button>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" onClick={handleSaveTreatment} disabled={saving}>
                {saving ? 'Saving…' : '💾 Save Treatment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AddPrescriptionInline({ treatmentId, onAdded, C, DARK, LIGHT }) {
  const [show, setShow]   = useState(false);
  const [form, setForm]   = useState({ medicineName: '', dosage: '', frequency: '', duration: '', instructions: '' });
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!form.medicineName) return;
    setSaving(true);
    await hospitalApi.addPrescription(treatmentId, form);
    setForm({ medicineName: '', dosage: '', frequency: '', duration: '', instructions: '' });
    setShow(false);
    setSaving(false);
    onAdded();
  };

  if (!show) {
    return (
      <button onClick={() => setShow(true)}
        style={{ fontSize: 12, padding: '4px 12px', background: LIGHT, color: DARK,
          border: `1px solid ${C}`, borderRadius: 6, cursor: 'pointer', fontWeight: 600, marginTop: 4 }}>
        + Add Prescription
      </button>
    );
  }

  const inp2 = { className: 'form-control', style: { fontSize: 12 } };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 2fr auto', gap: 6, marginTop: 8 }}>
      <input {...inp2} placeholder="Medicine" value={form.medicineName} onChange={e => setForm(f => ({ ...f, medicineName: e.target.value }))} />
      <input {...inp2} placeholder="Dosage" value={form.dosage} onChange={e => setForm(f => ({ ...f, dosage: e.target.value }))} />
      <input {...inp2} placeholder="Frequency" value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))} />
      <input {...inp2} placeholder="Duration" value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} />
      <input {...inp2} placeholder="Instructions" value={form.instructions} onChange={e => setForm(f => ({ ...f, instructions: e.target.value }))} />
      <div style={{ display: 'flex', gap: 4 }}>
        <button onClick={handleAdd} disabled={saving}
          style={{ padding: '6px 10px', background: C, color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>
          {saving ? '…' : '✓'}
        </button>
        <button onClick={() => setShow(false)}
          style={{ padding: '6px 10px', background: '#f1f3f4', color: '#5f6368', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>
          ✕
        </button>
      </div>
    </div>
  );
}
